import * as zod from 'zod'
import type { DataStatus } from '../Device'
import type { TypedCallback } from '../Reusable'
import type { Response, WorkerRequest } from '../WebWorker'
import { BarChartWidgetElementSchema, BarChartWidgetSchema, BAR_CHART_WIDGET_CONSTRUCTOR, DEFAULT_BAR_CHART_WIDGET_ELEMENT, type BarChartWidgetData, type BarChartWidgetDataRequest, type BarChartWidgetElementDialogProps, type BarChartWidgetElementPartial, type BarChartWidgetOptionsResponse, type BarChartWidgetPartial } from './BarChartWidget'
import { CardChartWidgetElementSchema, CardChartWidgetElementUnionSchema, CardChartWidgetSchema, CARD_CHART_WIDGET_CONSTRUCTOR, DEFAULT_CARD_CHART_WIDGET_ELEMENT, type CardChartWidgetData, type CardChartWidgetDataRequest, type CardChartWidgetElementDialogProps, type CardChartWidgetElementUnionPartial, type CardChartWidgetOptionsResponse, type CardChartWidgetPartial } from './CardChartWidget'
import { CollapsableTableWidgetElementSchema, CollapsableTableWidgetSchema, COLLAPSABLE_TABLE_WIDGET_CONSTRUCTOR, DEFAULT_COLLAPSABLE_TABLE_WIDGET_ELEMENT, type CollapsableTableOptionsResponse, type CollapsableTableWidgetData, type CollapsableTableWidgetDataRequest, type CollapsableTableWidgetElementDialogProps, type CollapsableTableWidgetElementPartial, type CollapsableTableWidgetPartial } from './CollapsableTable'
import type { IntervalLockSupport, IntervalSupport, MarkableWidget, WidgetElementBufferSupport, WidgetType } from './Common'
import { LINE_CHART_WIDGET_CONSTRUCTOR, DEFAULT_LINE_CHART_WIDGET_ELEMENT, LineChartWidgetElementSchema, LineChartWidgetSchema, type LineChartWidgetData, type LineChartWidgetDataRequest, type LineChartWidgetElementDialogProps, type LineChartWidgetElementPartial, type LineChartWidgetOptionsResponse, type LineChartWidgetPartial } from './LineChartWidget'
import { LINE_WIDGET_CONSTRUCTOR, DEFAULT_LINE_WIDGET_ELEMENT, LineWidgetElementSchema, LineWidgetSchema, type LineWidgetData, type LineWidgetDataRequest, type LineWidgetElementDialogProps, type LineWidgetElementPartial, type LineWidgetOptionsResponse, type LineWidgetPartial } from './LineWidget'
import { MULTIGRID_CHART_WIDGET_CONSTRUCTOR, DEFAULT_MULTIGRID_CHART_WIDGET_ELEMENT, MultigridChartWidgetElementSchema, MultigridChartWidgetSchema, type MultigridChartWidgetData, type MultigridChartWidgetDataRequest, type MultigridChartWidgetElementDialogProps, type MultigridChartWidgetElementPartial, type MultigridChartWidgetOptionsResponse, type MultigridChartWidgetPartial } from './MultigridChartWidget'
import { MULTIPLE_Y_CHART_WIDGET_CONSTRUCTOR, DEFAULT_MULTIPLE_Y_CHART_WIDGET_ELEMENT, MultipleYChartWidgetElementSchema, MultipleYChartWidgetSchema, SECOND_DEFAULT_MULTIPLE_Y_CHART_WIDGET_ELEMENT, type MultipleYChartWidgetData, type MultipleYChartWidgetDataRequest, type MultipleYChartWidgetElementDialogProps, type MultipleYChartWidgetElementPartial, type MultipleYChartWidgetOptionsResponse, type MultipleYChartWidgetPartial } from './MultipleYChartWidget'
import { PAGE_WIDGET_CONSTRUCTOR, DEFAULT_PAGE_WIDGET_ELEMENT, PageWidgetElementSchema, PageWidgetSchema, type PageWidgetData, type PageWidgetDataRequest, type PageWidgetElementDialogProps, type PageWidgetElementPartial, type PageWidgetOptionsResponse, type PageWidgetPartial } from './PageWidget'
import { PICTOGRAPH_WIDGET_CONSTRUCTOR, DEFAULT_PICTOGRAPH_WIDGET_ELEMENT, PictographWidgetElementSchema, PictographWidgetSchema, type PictographWidgetData, type PictographWidgetDataRequest, type PictographWidgetElementDialogProps, type PictographWidgetElementPartial, type PictographWidgetOptionsResponse, type PictographWidgetPartial } from './PictographWidget'
import { TABLE_WIDGET_CONSTRUCTOR, DEFAULT_TABLE_WIDGET_ELEMENT, TableWidgetElementSchema, TableWidgetSchema, type TableWidgetData, type TableWidgetDataRequest, type TableWidgetElementDialogProps, type TableWidgetElementPartial, type TableWidgetOptionsResponse, type TableWidgetPartial } from './TableWidget'
import { TIME_SELECTION_WIDGET_CONSTRUCTOR, TimeSelectionWidgetElementSchema, TimeSelectionWidgetSchema, type TimeSelectionWidgetDataRequest, type TimeSelectionWidgetElementDialogProps, type TimeSelectionWidgetElementPartial, type TimeSelectionWidgetOptionsResponse, type TimeSelectionWidgetPartial } from './TimeSelectionWidget'

type WidgetRequestTypeMap = {
  [T in WidgetType]: WidgetDataExchangeTypes<T>
}
type WidgetResponseBody<K extends WidgetType> = Response<WidgetRequestTypeMap[K]>['body']
type WidgetDataRequestBody<K extends WidgetType> = WidgetRequestBodyMap[K]
type WidgetDataRespnseBody<K extends WidgetType> = WidgetResponseBodyMap[K]

export type BaseWidgetDataRequest = {
  type: WidgetType
  tokenId?: string
  dashboardId?: string
}
export type WidgetDataResponse = {
  type: WidgetType
  errorCodes: DataStatus[]
  hasValues: boolean
}
export type WidgetDataExchangeTypes<K extends WidgetType> = `${K}-widget-data`
export type WidgetMap = {
  [P in WidgetType]: Extract<Widgets, { type: P }>
}
export type WidgetRequestBodyProcessor<K extends WidgetType> = (input: WidgetMap[K]) => WidgetRequestBodyMap[K]
export type WidgetResponseBodyProcessor<K extends WidgetType> = (input: WidgetResponseBody<K>) => WidgetResponseBody<K>

export type WidgetRequest<K extends WidgetType> = WorkerRequest<WidgetDataRequestBody<K>, WidgetDataRespnseBody<K>>

export type WidgetRequestBodyMap = {
  [P in WidgetType]: Extract<AbstractWidgetDataRequestBody, { type: P }>
}
export type WidgetResponseBodyMap = {
  [P in WidgetType]: Extract<AbstractWidgetDataResponseBody, { type: P }>
}
export type WidgetExchangeMap = {
  [P in WidgetType as WidgetRequestTypeMap[P]]: WidgetRequest<P>
}
type AbstractWidgetDataResponseBody
  = | LineWidgetOptionsResponse
    | PageWidgetOptionsResponse
    | LineChartWidgetOptionsResponse
    | BarChartWidgetOptionsResponse
    | MultigridChartWidgetOptionsResponse
    | MultipleYChartWidgetOptionsResponse
    | CardChartWidgetOptionsResponse
    | PictographWidgetOptionsResponse
    | TableWidgetOptionsResponse
    | CollapsableTableOptionsResponse
    | TimeSelectionWidgetOptionsResponse

type AbstractWidgetDataRequestBody
  = | LineWidgetDataRequest
    | PageWidgetDataRequest
    | LineChartWidgetDataRequest
    | BarChartWidgetDataRequest
    | MultigridChartWidgetDataRequest
    | MultipleYChartWidgetDataRequest
    | CardChartWidgetDataRequest
    | PictographWidgetDataRequest
    | TableWidgetDataRequest
    | CollapsableTableWidgetDataRequest
    | TimeSelectionWidgetDataRequest

export const WidgetElementUnionSchema = zod.discriminatedUnion('type', [
  PageWidgetElementSchema, LineWidgetElementSchema, PictographWidgetElementSchema,
  BarChartWidgetElementSchema, TableWidgetElementSchema, CardChartWidgetElementSchema,
  MultipleYChartWidgetElementSchema, LineChartWidgetElementSchema, MultigridChartWidgetElementSchema,
  CollapsableTableWidgetElementSchema, TimeSelectionWidgetElementSchema,
]).superRefine((input, ctx) => {
  if (input.type === 'CARD_CHART') {
    const results = CardChartWidgetElementUnionSchema.safeParse(input)
    if (results.error) {
      results.error.issues.forEach(ctx.addIssue)
      return zod.NEVER
    }
  }
})
export type WidgetElementUnion = zod.infer<typeof WidgetElementUnionSchema>

export const WidgetsSchema = zod.discriminatedUnion('type', [
  PageWidgetSchema, LineWidgetSchema, PictographWidgetSchema, BarChartWidgetSchema,
  CardChartWidgetSchema, TableWidgetSchema, MultipleYChartWidgetSchema, LineChartWidgetSchema,
  MultigridChartWidgetSchema, CollapsableTableWidgetSchema, TimeSelectionWidgetSchema,
])
export type Widgets = zod.infer<typeof WidgetsSchema>
export type PartialWidgetElements = BarChartWidgetElementPartial | CardChartWidgetElementUnionPartial | CollapsableTableWidgetElementPartial
  | LineChartWidgetElementPartial | LineWidgetElementPartial | MultigridChartWidgetElementPartial
  | MultipleYChartWidgetElementPartial | PageWidgetElementPartial | PictographWidgetElementPartial
  | TableWidgetElementPartial | TimeSelectionWidgetElementPartial

export type PartialWidgets = BarChartWidgetPartial | CardChartWidgetPartial | CollapsableTableWidgetPartial
  | LineChartWidgetPartial | LineWidgetPartial | MultigridChartWidgetPartial
  | MultipleYChartWidgetPartial | PageWidgetPartial | PictographWidgetPartial
  | TableWidgetPartial | TimeSelectionWidgetPartial

/**
 * Stricter record type where we strictly have the value matching the widget type key.
 * assignable to Record<WidgetType, PartialWidgets> for index access based on widget type
 */
export type WidgetsMap = {
  [P in WidgetType]: Extract<PartialWidgets, { type: P }>
}
export type WidgetElementsMap = {
  [P in WidgetType]: Extract<PartialWidgetElements, { type: P }>[]
}

export type WidgetsData = PageWidgetData | LineWidgetData | PictographWidgetData
  | BarChartWidgetData | CardChartWidgetData | TableWidgetData | MultipleYChartWidgetData | LineChartWidgetData
  | MultigridChartWidgetData | CollapsableTableWidgetData

export type WidgetElementDialogProps = PageWidgetElementDialogProps | LineWidgetElementDialogProps | PictographWidgetElementDialogProps
  | BarChartWidgetElementDialogProps | CardChartWidgetElementDialogProps | TableWidgetElementDialogProps
  | MultipleYChartWidgetElementDialogProps | LineChartWidgetElementDialogProps | CollapsableTableWidgetElementDialogProps
  | MultigridChartWidgetElementDialogProps | TimeSelectionWidgetElementDialogProps

export type WidgetElementDialogData = {
  settings: WidgetElementDialogProps
  element: WidgetElementUnion
  callback: TypedCallback<WidgetElementUnion>
}

export function supportsBufferOperations(input: WidgetElementUnion): input is WidgetElementUnion & WidgetElementBufferSupport {
  return 'buffer' in input
}
export function allowsLineSelection(input: WidgetElementUnion): input is WidgetElementUnion & { lineNumber: number } {
  return 'lineNumber' in input
}
export function hasIntervalSupport(input: Widgets): input is Widgets & IntervalSupport {
  return 'interval' in input
}
export function hasIntervalLockSupport(input: Widgets): input is Widgets & IntervalLockSupport {
  return 'dashboardIntervalEnabled' in input
}
export function hasMarkableAreaSupport(input: Widgets): input is Widgets & MarkableWidget {
  return 'markAreaElements' in input
}
/**
 * Creates a copy of the default widgets map, containing default settings for all the widgets
 */
export function makeDefaultWidgetsMap(): WidgetsMap {
  const map: WidgetsMap = {
    MULTIGRID_CHART: MULTIGRID_CHART_WIDGET_CONSTRUCTOR(),
    LINE_CHART: LINE_CHART_WIDGET_CONSTRUCTOR(),
    MULTIPLE_Y_CHART: MULTIPLE_Y_CHART_WIDGET_CONSTRUCTOR(),
    PAGE: PAGE_WIDGET_CONSTRUCTOR(),
    LINE: LINE_WIDGET_CONSTRUCTOR(),
    TABLE: TABLE_WIDGET_CONSTRUCTOR(),
    BAR_CHART: BAR_CHART_WIDGET_CONSTRUCTOR(),
    PICTOGRAPH: PICTOGRAPH_WIDGET_CONSTRUCTOR(),
    CARD_CHART: CARD_CHART_WIDGET_CONSTRUCTOR(),
    COLLAPSABLE_TABLE: COLLAPSABLE_TABLE_WIDGET_CONSTRUCTOR(),
    TIME_SELECTION: TIME_SELECTION_WIDGET_CONSTRUCTOR(),
  }
  return makeClone(map)
}

export function makeDefaultWidgetElementsMap(): WidgetElementsMap {
  return {
    MULTIGRID_CHART: [DEFAULT_MULTIGRID_CHART_WIDGET_ELEMENT],
    LINE_CHART: [DEFAULT_LINE_CHART_WIDGET_ELEMENT],
    MULTIPLE_Y_CHART: [DEFAULT_MULTIPLE_Y_CHART_WIDGET_ELEMENT, SECOND_DEFAULT_MULTIPLE_Y_CHART_WIDGET_ELEMENT],
    PAGE: [DEFAULT_PAGE_WIDGET_ELEMENT],
    LINE: [DEFAULT_LINE_WIDGET_ELEMENT],
    TABLE: [DEFAULT_TABLE_WIDGET_ELEMENT],
    BAR_CHART: [DEFAULT_BAR_CHART_WIDGET_ELEMENT],
    PICTOGRAPH: [DEFAULT_PICTOGRAPH_WIDGET_ELEMENT],
    CARD_CHART: [DEFAULT_CARD_CHART_WIDGET_ELEMENT],
    COLLAPSABLE_TABLE: [DEFAULT_COLLAPSABLE_TABLE_WIDGET_ELEMENT],
    TIME_SELECTION: [],
  }
}
